import React from "react";
import { Box, SpaceBetween } from "@amzn/awsui-components-react";
import { InvestigationTrendPoint } from "../../lusca-search-api/generated-src";
import {
  describeTrend,
  formatGrowth,
  pointTime,
  pointValue,
  recentTrend,
  trendGrowth,
} from "./trendUtils";

const WIDTH = 72;
const HEIGHT = 18;
const PAD = 2;

/** SVG polyline coordinates for the series, scaled into the sparkline box. */
export function sparklinePath(points: InvestigationTrendPoint[]): string {
  if (points.length < 2) return "";
  const xs = points.map(pointTime);
  const ys = points.map(pointValue);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  const [y0, y1] = [Math.min(...ys), Math.max(...ys)];
  const sx = (x: number) =>
    PAD + ((x - x0) / (x1 - x0 || 1)) * (WIDTH - 2 * PAD);
  // A flat series sits in the middle rather than on the floor.
  const sy = (y: number) =>
    y1 === y0
      ? HEIGHT / 2
      : HEIGHT - PAD - ((y - y0) / (y1 - y0)) * (HEIGHT - 2 * PAD);
  return points
    .map((p, i) => `${sx(xs[i]).toFixed(1)},${sy(pointValue(p)).toFixed(1)}`)
    .join(" ");
}

interface TrendSparklineProps {
  trend?: InvestigationTrendPoint[] | null;
  /** Show the signed change next to the line (default true). */
  showDelta?: boolean;
}

/**
 * Last 7 days of snapshots as a tiny line plus the signed change. Growing populations read red,
 * shrinking green, flat grey; "—" until there are two snapshots.
 */
export const TrendSparkline: React.FC<TrendSparklineProps> = ({
  trend,
  showDelta = true,
}) => {
  const points = recentTrend(trend);
  const growth = trendGrowth(trend);
  const label = describeTrend(trend);
  if (growth === null) {
    return (
      <Box color="text-body-secondary" display="inline">
        <span title={label}>—</span>
      </Box>
    );
  }
  const color =
    growth > 0
      ? "text-status-error"
      : growth < 0
      ? "text-status-success"
      : "text-body-secondary";
  return (
    <Box color={color} display="inline">
      <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
        <svg
          width={WIDTH}
          height={HEIGHT}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label={label}
          style={{ display: "block" }}
        >
          <title>{label}</title>
          <polyline
            points={sparklinePath(points)}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        {showDelta && (
          <span style={{ whiteSpace: "nowrap" }} data-testid="trend-delta">
            {formatGrowth(growth)}
          </span>
        )}
      </SpaceBetween>
    </Box>
  );
};

export default TrendSparkline;
